import React from 'react';
import { logError } from '@utils/usefulFuncs';
import { fetchTenant, fetchVocabularies, isSetupActive } from '@services/fmdc';
import { useFmdcPoll } from './useFmdcPoll';

// The tenant, once, at the page root. Every view reads `canAdmin`, the template version and the
// catalog state from here instead of fetching GET /tenant itself, and the gate below decides what
// the page shows before any sub-route renders.
//
// Also owns the vocabularies (GET /vocabularies): small, stable for a session, and wanted by the
// correct form, the map form and the catalog form, so they are fetched once on first ask.

export const EMBEDDINGS_POLL_CEILING_MS = 10 * 60 * 1000;

export const TENANT_GATES = {
  LOADING: 'loading',
  ERROR: 'error',
  NOT_SET_UP: 'not_set_up',
  SETTING_UP: 'setting_up',
  DISABLED: 'disabled',
  READY: 'ready',
};

// Pure, so it can be tested without a provider. `pendingSetup` is the 202 body from POST
// /tenant/setup, held until GET /tenant reflects it — the handoff says `tenant` is null until the
// first setup, and the setup object may arrive on the tenant or alone.
export const tenantGate = ({ tenant, isLoading, error, pendingSetup }) => {
  if (isLoading) return TENANT_GATES.LOADING;
  if (error && !tenant) return TENANT_GATES.ERROR;
  if (!tenant) {
    return pendingSetup && isSetupActive(pendingSetup) ? TENANT_GATES.SETTING_UP : TENANT_GATES.NOT_SET_UP;
  }
  // POST /tenant/setup creates the tenant row at once with enabled: false, the requested
  // template_version and setup.status "queued"; the bootstrap flips enabled to true when it
  // succeeds. So "switched off with a setup in flight" IS the first setup, not a disabled client,
  // and a tenant that never got a catalog after a failed first setup is still "not set up".
  const catalogItems = Number(tenant.catalog?.items) || 0;
  if (tenant.enabled === false && isSetupActive(tenant.setup)) return TENANT_GATES.SETTING_UP;
  if (!tenant.template_version && isSetupActive(tenant.setup)) return TENANT_GATES.SETTING_UP;
  if (tenant.enabled === false && tenant.setup?.status === 'failed' && catalogItems === 0)
    return TENANT_GATES.NOT_SET_UP;
  if (tenant.enabled === false) return TENANT_GATES.DISABLED;
  return TENANT_GATES.READY;
};

const defaultState = {
  tenant: null,
  canAdmin: false,
  isLoading: true,
  error: null,
  gate: TENANT_GATES.LOADING,
  pendingSetup: null,
  vocabularies: null,
  vocabulariesError: null,
  reload: () => logError('FmdcTenantContext: reload called without a Provider'),
  setPendingSetup: () => logError('FmdcTenantContext: setPendingSetup called without a Provider'),
  loadVocabularies: () => logError('FmdcTenantContext: loadVocabularies called without a Provider'),
};

export const FmdcTenantContext = React.createContext(defaultState);

export const FmdcTenantProvider = ({ children }) => {
  const [tenant, setTenant] = React.useState(null);
  const [canAdmin, setCanAdmin] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [pendingSetup, setPendingSetup] = React.useState(null);
  const [vocabularies, setVocabularies] = React.useState(null);
  const [vocabulariesError, setVocabulariesError] = React.useState(null);
  const vocabulariesRequested = React.useRef(false);

  // `guard` is the mount effect's cancel flag; a poll or a caller passes nothing.
  const reload = React.useCallback(async (guard) => {
    const result = await fetchTenant();
    if (guard?.isCanceled) return result;
    if (result.error) {
      setError(result.error);
      // Keep what we had on a transient error so an open page does not flash the not-set-up gate.
    } else {
      setError(null);
      setTenant(result.tenant);
      setCanAdmin(result.canAdmin);
      // Once the server reflects the setup, the 202 body has done its job.
      if (result.tenant) setPendingSetup(null);
    }
    setIsLoading(false);
    return result;
  }, []);

  React.useEffect(() => {
    const guard = { isCanceled: false };
    reload(guard);
    return () => {
      guard.isCanceled = true;
    };
  }, [reload]);

  // Keep polling while something server-side is still moving: a setup in flight, or a catalog
  // whose embeddings are being refreshed after an admin edit. The refresh takes seconds; if the
  // flag is still up after ten minutes the job has died, and polling forever from every open tab
  // would not revive it.
  const setup = tenant?.setup || pendingSetup;
  const embeddingsStale = tenant?.catalog?.embeddings_stale === true;
  const staleSinceRef = React.useRef(null);
  if (embeddingsStale && staleSinceRef.current === null) staleSinceRef.current = Date.now();
  if (!embeddingsStale) staleSinceRef.current = null;
  const staleWithinCeiling = embeddingsStale && Date.now() - staleSinceRef.current < EMBEDDINGS_POLL_CEILING_MS;
  const shouldPoll = !isLoading && (isSetupActive(setup) || staleWithinCeiling);
  useFmdcPoll(reload, { enabled: shouldPoll });

  const loadVocabularies = React.useCallback(async () => {
    if (vocabulariesRequested.current) return;
    vocabulariesRequested.current = true;
    const result = await fetchVocabularies();
    if (result.error) {
      setVocabulariesError(result.error);
      // Allow a retry on the next ask.
      vocabulariesRequested.current = false;
    } else {
      setVocabulariesError(null);
      setVocabularies(result.vocabularies || {});
    }
  }, []);

  const gate = tenantGate({ tenant, isLoading, error, pendingSetup });

  const value = React.useMemo(
    () => ({
      tenant,
      canAdmin,
      isLoading,
      error,
      gate,
      pendingSetup,
      vocabularies,
      vocabulariesError,
      reload,
      setPendingSetup,
      loadVocabularies,
    }),
    [tenant, canAdmin, isLoading, error, gate, pendingSetup, vocabularies, vocabulariesError, reload, loadVocabularies]
  );

  return <FmdcTenantContext.Provider value={value}>{children}</FmdcTenantContext.Provider>;
};

// `[{ value, label }]` for a Select, from one vocabulary list. Proposed entries are accepted on
// input but may surprise a reviewer, so the suffix is the whole mitigation.
export const vocabularyOptions = (vocabularies, listName) => {
  const list = vocabularies?.[listName];
  if (!Array.isArray(list)) return [];
  return list.map((entry) => {
    const entryValue = typeof entry === 'string' ? entry : entry.value;
    const isProposed = typeof entry === 'object' && entry !== null && entry.status === 'proposed';
    return { value: entryValue, label: isProposed ? `${entryValue} (proposed)` : entryValue };
  });
};

// Loads the vocabularies on first use and returns them with their error. Views that need the
// dropdowns call this rather than the context directly.
export const useVocabularies = () => {
  const { vocabularies, vocabulariesError, loadVocabularies } = React.useContext(FmdcTenantContext);
  React.useEffect(() => {
    if (!vocabularies) loadVocabularies();
  }, [vocabularies, loadVocabularies]);
  return { vocabularies, error: vocabulariesError, isLoading: !vocabularies && !vocabulariesError };
};
